import { Fragment, ReactNode, useEffect, useRef } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from '@v2/shared/lib/utils';
import { useSite } from '@v2/shared/lib/site';

export type ModalProps = {
  isOpen: boolean;
  close: () => void;
  title: string;
  /**
   * Below `md`, size the panel to its content — up to the height of the screen — and
   * centre it over the shaded page rather than filling it. Above `md` it changes nothing.
   *
   * Opt-in, because full-screen is the right answer for the flows this was built for:
   * the auto-renewal dialogs are the task, and the account page behind them is not
   * something the customer is reading past them. An offer is the other case — what it
   * is offered *against* is the page, and hiding it makes the offer the whole world.
   */
  sizeToContentOnMobile?: boolean;
  children?: ReactNode;
};

/**
 * A centred dialog with a title bar, full-screen on a phone unless asked otherwise.
 *
 * Off headless_jshop, where the account pages' flows lived in it. The Full layout's
 * FullScreenDialog is the other shape — a panel that slides in from the edge for the
 * mobile menus — and neither is a variant of the other, so they are separate components
 * rather than one with a mode.
 *
 * Focus starts on the close button rather than wherever the panel's first control
 * happens to be: these dialogs open onto destructive flows, and the first thing under the
 * cursor should not be the one that cancels a subscription.
 */
export default function Modal({ isOpen, close, title, sizeToContentOnMobile, children }: ModalProps) {
  const closeButtonRef = useRef(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const { brand } = useSite();

  /*
   * Let a finger scroll this panel on iOS, which HeadlessUI otherwise forbids.
   *
   * On iOS — and only iOS, `utils/platform.isIOS` — locking the page scroll installs two
   * handlers on `document` (`document-overflow/handle-ios-locking`). Any touch whose
   * target is not inside a container the lock recognises gets `touch-action: none`
   * written onto it by the `touchstart` one, and every `touchmove` after it is
   * `preventDefault`ed. The container it recognises is the `Dialog.Panel` of whichever
   * dialog *took* the lock.
   *
   * That is normally the panel being touched. It is not here, because this modal opens
   * while the cart drawer's dialog is still holding the lock: `beginCheckout` closes the
   * drawer and shows the offer in the same commit, and the drawer keeps its lock for the
   * 200ms of its leave transition. `overflow-store` only re-runs the setup on a
   * *transition* into the locked state, so a lock count going 1 -> 2 re-runs nothing —
   * the handlers stay bound to the drawer, whose panel is by then `display: none` and
   * contains nothing. Every touch in here takes the "not in a container" branch, so the
   * offers could not be scrolled by a finger at all, only by assigning `scrollTop`. That
   * is why this survived a headless-Chromium check: none of it runs off iOS.
   *
   * Stopping the touches at the panel is what unbinds us from that decision. The two
   * handlers are on `document` and listen in the bubble phase, so an event that does not
   * leave the panel never reaches them, and the browser is left to scroll the box under
   * the finger by itself. Taps on the shaded page still reach it — the backdrop is
   * outside the panel — so closing by tapping outside still declines.
   */
  useEffect(() => {
    const panel = panelRef.current;

    if (!isOpen || !sizeToContentOnMobile || !panel) {
      return undefined;
    }

    const keepInsideThePanel = (event: TouchEvent) => event.stopPropagation();

    panel.addEventListener('touchstart', keepInsideThePanel);
    panel.addEventListener('touchmove', keepInsideThePanel);

    return () => {
      panel.removeEventListener('touchstart', keepInsideThePanel);
      panel.removeEventListener('touchmove', keepInsideThePanel);
    };
  }, [isOpen, sizeToContentOnMobile]);

  return (
    <Transition show={isOpen} as={Fragment}>
      {/*
        v2-root is not decoration: HeadlessUI renders a Dialog through a portal onto
        document.body, and every V2 rule is compiled scoped under .v2-root (see
        src/DMagzWebV2/postcss/scopeV2.js). On a page where the class sits on the mount
        wrapper rather than on <html>, the portal lands outside it and the dialog opens
        completely unstyled. Carrying the class here re-establishes the scope wherever the
        portal happens to land; nesting it inside another .v2-root is harmless.

        data-brand rides along for a related reason: the brand palettes are custom
        properties declared on the scope class itself, so a .v2-root that does not name
        its brand re-declares the base palette and resets whatever it inherited. Same
        reasoning, same fix, as Layouts/Full/Navigation/FullScreenDialog.
      */}
      <Dialog className="v2-root" data-brand={brand} onClose={close} initialFocus={closeButtonRef}>
        <Transition.Child
          as={Fragment}
          enter="transition duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/80 z-[260]" aria-hidden="true" />
        </Transition.Child>

        <Transition.Child
          className={cn('fixed inset-0 z-[270]', sizeToContentOnMobile && 'overflow-y-auto')}
          enter="transition duration-200 ease-out"
          enterFrom="transform scale-95 opacity-0"
          enterTo="transform scale-100 opacity-100"
          leave="transition duration-175 ease-out"
          leaveFrom="transform scale-100 opacity-100"
          leaveTo="transform scale-95 opacity-0"
        >
          {/*
            Scrolled by the box above and centred by this one, which is what keeps the top
            of a panel reachable if one ever does outgrow the screen: centring inside the
            scroll container itself puts that overflow above `scrollTop: 0`. Same split,
            for the same reason, as `Pages/MegaSale/SaleEndedDialog`.

            The padding is the gutter the shaded page shows through. It is dropped at
            `md` because the panel's width is a percentage of this box, so 16px of
            padding there would narrow it.
          */}
          <div
            className={cn(
              'flex items-center justify-center',
              sizeToContentOnMobile ? 'min-h-full p-4 md:p-0' : 'h-full'
            )}
          >
            {/*
              `calc(100dvh - 2rem)` is the screen less the two gutters above, and it is the
              whole of what keeps a tall panel on it: a cart with ten magazines in it makes
              ten offers, and sized to that content alone the panel ran 1234px down an 844px
              phone with its button somewhere past the fold. Capped, it stops at the gutter
              and the body below scrolls instead — the arrangement `md` has always had.

              The `2rem` is that `p-4` doubled and has to move with it, and it cannot be
              `max-h-full`: this box's height is `min-height`, not `height`, so a percentage
              has nothing definite to resolve against and Chrome drops it. `dvh` rather than
              `vh` because `vh` is the screen with the address bar hidden, which is taller
              than the `fixed inset-0` this sits in whenever the bar is showing.
            */}
            <Dialog.Panel
              ref={panelRef}
              className={cn(
                'bg-white md:w-3/5 md:h-auto md:max-h-[90vh] lg:w-1/3 md:rounded-md md:flex md:flex-col',
                'overflow-hidden relative',
                sizeToContentOnMobile
                  ? 'w-full max-w-md rounded-md md:max-w-none flex flex-col max-h-[calc(100dvh_-_2rem)]'
                  : 'w-full h-full rounded-none'
              )}
            >
              <Dialog.Title
                className={cn(
                  'p-2 pl-3 bg-white flex justify-between items-center',
                  'border-b-[1.5px] border-solid border-x-0 border-t-0 border-slate-200 flex-shrink-0'
                )}
              >
                <span className="font-semibold text-xl">{title}</span>
                <button
                  type="button"
                  className="w-10 h-10 border-0 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center hover:text-slate-700 hover:bg-slate-200 active:text-slate-800 active:bg-slate-300"
                  onClick={close}
                  aria-label="Close"
                  ref={closeButtonRef}
                >
                  <FontAwesomeIcon icon={faTimes} size="lg" />
                </button>
              </Dialog.Title>

              {/*
                Either way the body is the scrolling box; the two differ only in how it is
                given a height to scroll inside. A full-screen panel measures it out by
                hand — `top-[57.5px]` is the title bar's height, and pinning the body
                between that and the panel's bottom is what bounds it. A sized panel is a
                flex column, so `flex-1` bounds it against the cap instead, and the bar
                above stays whatever height it wants to be.

                No `min-h-0` needed to make that shrink: `overflow-y-auto` already drops a
                flex item's automatic minimum size to zero.

                `overscroll-contain` is the one thing the scroll lock would have given this
                box itself, had it recognised the panel: it sets `overscrollBehavior:
                'contain'` on every container it does recognise, so that carrying a scroll
                past the end of one does not start dragging the page underneath it. Since
                the effect above is what keeps the lock out of here, the property it would
                have set has to be stated. Below `md` only, so a mouse wheel on a desktop
                still chains exactly as it does today.
              */}
              <div
                className={cn(
                  'md:flex-1 md:relative md:inset-auto md:top-auto md:overflow-y-auto z-[280]',
                  sizeToContentOnMobile
                    ? 'flex-1 overflow-y-auto overscroll-contain md:overscroll-auto'
                    : 'overflow-y-auto absolute inset-x-0 top-[57.5px] bottom-0'
                )}
              >
                {children}
              </div>
            </Dialog.Panel>
          </div>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
}
